if (!customElements.get('wlsn-video')) {
  class WLSNVideo extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        playButton: 'js-wlsn-video-play-button',
        muteButton: 'js-wlsn-video-mute-unmute-button',
        playing: 'playing',
        muted: 'muted',
        slider: 'wlsn-slider',
      };

      this.observer = new IntersectionObserver(this._handleIntersection.bind(this), {
        threshold: 0.5,
      });

      this.boundVideoPlayingHandler = this._videoPlayingHandler.bind(this);
      this.boundHandleVideoEnded = this._handleVideoEnded.bind(this);
      this.boundHandlePlaying = this._handlePlaying.bind(this);

      // Bound handlers for native video events (when controls are present)
      this.boundHandleNativePlay = null;
      this.boundHandleNativePause = null;

      this.boundHandleSliderAutoplayPlay = this._handleSliderAutoplayPlay.bind(this);
      this.boundHandleSliderAutoplayPause = this._handleSliderAutoplayPause.bind(this);
      this.boundHandleLoopTimeUpdate = this._handleLoopTimeUpdate.bind(this);
      this.boundHandleMuteUnmuteClick = this._handleMuteUnmuteClick.bind(this);
      this.boundHandleVolumeChange = this._handleVolumeChangeForMutedClass.bind(this);

      /** @type {number | null} */
      this._loopTimeLastPosition = null;
      this._loopFirstCycleAdvanced = false;
    }

    /**
     * Get DOM elements
     * @returns {object} - Object containing DOM element references
     */
    get elements() {
      return {
        videos: this.querySelectorAll('video'),
        playButton: this.querySelector(`.${this.classes.playButton}`),
        muteButton: this.querySelector(`.${this.classes.muteButton}`),
      };
    }

    /**
     * Get the currently visible video element
     * @returns {HTMLVideoElement|undefined} - The visible video element
     */
    getVisibleVideo() {
      return Array.from(this.elements.videos).find((video) => getComputedStyle(video).display !== 'none');
    }

    /**
     * Connected callback lifecycle method
     * @returns {void}
     */
    connectedCallback() {
      const video = this.getVisibleVideo();
      if (video) {
        const hasControls = video.hasAttribute('controls');
        this.observer.observe(this);

        // Add ended event handler to reset state
        video.addEventListener('ended', this.boundHandleVideoEnded);

        this._attachParentSwiperAutoplaySync();
        this._attachLoopCompleteSlideAdvance();

        // If video has native controls, let browser handle play/pause
        // We just listen to native events to update our CSS classes
        if (hasControls) {
          // Create and store bound handlers
          this.boundHandleNativePlay = () => {
            this.classList.add(this.classes.playing);
          };

          this.boundHandleNativePause = () => {
            this.classList.remove(this.classes.playing);
          };

          // Listen to native play/pause events
          video.addEventListener('play', this.boundHandleNativePlay);
          video.addEventListener('pause', this.boundHandleNativePause);
        } else {
          // No native controls - use our custom click handler
          video.addEventListener('click', this.boundVideoPlayingHandler);
        }

        // Handle autoplay videos
        if (video.hasAttribute('autoplay')) {
          this._handleAutoplayVideo();
        } else if (this.elements.playButton) {
          this.elements.playButton.addEventListener('click', this.boundVideoPlayingHandler);
        }

        this.elements.videos.forEach((v) => {
          v.addEventListener('volumechange', this.boundHandleVolumeChange);
        });

        this._syncMutedClassFromVisibleVideo();

        if (this.elements.muteButton) {
          this.elements.muteButton.addEventListener('click', this.boundHandleMuteUnmuteClick);
        }
      }
    }

    /**
     * Disconnected callback lifecycle method
     * @returns {void}
     */
    disconnectedCallback() {
      this._detachParentSwiperAutoplaySync();
      this._detachLoopCompleteSlideAdvance();

      const video = this.getVisibleVideo();
      if (video) {
        video.removeEventListener('ended', this.boundHandleVideoEnded);
        video.removeEventListener('playing', this.boundHandlePlaying);
        video.removeEventListener('click', this.boundVideoPlayingHandler);

        // Remove native event listeners if they were added
        if (this.boundHandleNativePlay) {
          video.removeEventListener('play', this.boundHandleNativePlay);
        }
        if (this.boundHandleNativePause) {
          video.removeEventListener('pause', this.boundHandleNativePause);
        }
      }
      if (this.elements.playButton) {
        this.elements.playButton.removeEventListener('click', this.boundVideoPlayingHandler);
      }
      this.elements.videos.forEach((v) => {
        v.removeEventListener('volumechange', this.boundHandleVolumeChange);
      });
      if (this.elements.muteButton) {
        this.elements.muteButton.removeEventListener('click', this.boundHandleMuteUnmuteClick);
      }
      this.observer.disconnect();
    }

    /**
     * Handle autoplay video setup
     * @returns {void}
     */
    _handleAutoplayVideo() {
      if (this.elements.playButton) {
        this.elements.playButton.style.pointerEvents = 'none';
      }
      const video = this.getVisibleVideo();
      if (video) {
        video.addEventListener('playing', this.boundHandlePlaying);
      }
    }

    /**
     * Handle video playing event
     * @returns {void}
     */
    _handlePlaying() {
      this.classList.add(this.classes.playing);
    }

    /**
     * Handle intersection observer callback
     * @param {Array<IntersectionObserverEntry>} entries - Intersection observer entries
     * @returns {void}
     */
    _handleIntersection(entries) {
      entries.forEach((entry) => {
        const video = this.getVisibleVideo();
        if (video && !entry.isIntersecting && !video.paused) {
          this._videoPause();
        }
      });
    }

    /**
     * Pause all other synchronized videos
     * @returns {void}
     */
    _pauseSyncedVideos() {
      if (this.hasAttribute('data-sync-pause')) {
        const allSyncedVideos = document.querySelectorAll('wlsn-video[data-sync-pause]');
        allSyncedVideos.forEach((videoElement) => {
          if (videoElement !== this) {
            const v = videoElement.getVisibleVideo ? videoElement.getVisibleVideo() : null;
            if (v && !v.paused) {
              videoElement.classList.remove(this.classes.playing);
              v.pause();
            }
          }
        });
      }
    }

    /**
     * Handle video playing and pausing
     * @returns {void}
     */
    _videoPlayingHandler() {
      const video = this.getVisibleVideo();
      if (!video) return;
      if (video.paused) {
        this._pauseSyncedVideos();
        this.videoPlay();
      } else {
        this._videoPause();
      }
    }

    /**
     * Play the video (public method)
     * @returns {void}
     */
    videoPlay() {
      const video = this.getVisibleVideo();
      if (video) {
        video.play();
        this.classList.add(this.classes.playing);
      }
    }

    /**
     * Pause the video
     * @returns {void}
     */
    _videoPause() {
      const video = this.getVisibleVideo();
      if (video && !video.hasAttribute('autoplay')) {
        video.pause();
        this.classList.remove(this.classes.playing);
      }
    }

    /**
     * Toggle mute on all video sources; keep host `muted` class in sync
     * @param {Event} event
     * @returns {void}
     */
    _handleMuteUnmuteClick(event) {
      event.preventDefault();
      event.stopPropagation();
      const visible = this.getVisibleVideo();
      if (!visible) return;
      const nextMuted = !visible.muted;
      this.elements.videos.forEach((v) => {
        v.muted = nextMuted;
      });
      this._applyMutedStateToHost(nextMuted);
    }

    /**
     * Sync host class when volume/mute changes (e.g. programmatically)
     * @returns {void}
     */
    _handleVolumeChangeForMutedClass() {
      const visible = this.getVisibleVideo();
      if (!visible) return;
      this._applyMutedStateToHost(visible.muted);
    }

    /**
     * @param {boolean} isMuted
     * @returns {void}
     */
    _applyMutedStateToHost(isMuted) {
      if (isMuted) {
        this.classList.add(this.classes.muted);
      } else {
        this.classList.remove(this.classes.muted);
      }
      const btn = this.elements.muteButton;
      if (btn) {
        btn.setAttribute('aria-pressed', isMuted ? 'true' : 'false');
        btn.setAttribute('aria-label', isMuted ? 'Unmute video' : 'Mute video');
      }
    }

    /**
     * Align host `muted` class with the visible video element
     * @returns {void}
     */
    _syncMutedClassFromVisibleVideo() {
      const visible = this.getVisibleVideo();
      if (!visible) return;
      this._applyMutedStateToHost(visible.muted);
    }

    /**
     * Handle video ended event, reset to initial state
     * @returns {void}
     */
    _handleVideoEnded() {
      this.classList.remove(this.classes.playing);
      this._getSwiperInstance()?.autoplay?.start();
    }

    /**
     * Pause parent wlsn-slider Swiper autoplay while this video plays
     * @returns {void}
     */
    _handleSliderAutoplayPlay() {
      this._getSwiperInstance()?.autoplay?.stop();
      const video = this.getVisibleVideo();
      if (video?.loop) {
        this._loopFirstCycleAdvanced = false;
        this._loopTimeLastPosition = null;
      }
    }

    /**
     * Resume parent wlsn-slider Swiper autoplay when this video pauses
     * @returns {void}
     */
    _handleSliderAutoplayPause() {
      this._getSwiperInstance()?.autoplay?.start();
    }

    /**
     * @returns {void}
     */
    _attachParentSwiperAutoplaySync() {
      if (!this.closest(`${this.classes.slider}`)) return;
      this.elements.videos.forEach((v) => {
        v.addEventListener('play', this.boundHandleSliderAutoplayPlay);
        v.addEventListener('pause', this.boundHandleSliderAutoplayPause);
      });
    }

    /**
     * @returns {void}
     */
    _detachParentSwiperAutoplaySync() {
      this.elements.videos.forEach((v) => {
        v.removeEventListener('play', this.boundHandleSliderAutoplayPlay);
        v.removeEventListener('pause', this.boundHandleSliderAutoplayPause);
      });
    }

    /**
     * First loop iteration does not fire `ended`; detect wrap via time jump and go to next slide once.
     * @param {Event} event
     * @returns {void}
     */
    _handleLoopTimeUpdate(event) {
      const video = event.target;
      if (!(video instanceof HTMLVideoElement) || !video.loop) return;
      if (!this.closest(`${this.classes.slider}`)) return;

      const swiper = this._getParentSwiperInstance();
      if (!swiper) return;

      const t = video.currentTime;
      const last = this._loopTimeLastPosition;
      const dur = video.duration;
      if (!Number.isFinite(dur) || dur <= 0) {
        this._loopTimeLastPosition = t;
        return;
      }

      if (last != null && last > dur * 0.25 && t < last - 0.5) {
        if (!this._loopFirstCycleAdvanced) {
          this._loopFirstCycleAdvanced = true;
          swiper.slideNext();
          this._getSwiperInstance()?.autoplay?.start();
        }
      }

      this._loopTimeLastPosition = t;
    }

    /**
     * @returns {void}
     */
    _attachLoopCompleteSlideAdvance() {
      if (!this.closest(`${this.classes.slider}`)) return;
      this.elements.videos.forEach((v) => {
        if (v.loop) {
          v.addEventListener('timeupdate', this.boundHandleLoopTimeUpdate);
        }
      });
    }

    /**
     * @returns {void}
     */
    _detachLoopCompleteSlideAdvance() {
      this.elements.videos.forEach((v) => {
        v.removeEventListener('timeupdate', this.boundHandleLoopTimeUpdate);
      });
    }

    /**
     * Parent Swiper (any autoplay), for programmatic slide change
     * @returns {object | null | undefined}
     */
    _getParentSwiperInstance() {
      const slider = this.closest(`${this.classes.slider}`);
      const swiper = slider?.swiperInstance;
      if (!swiper || swiper.destroyed) return null;
      return swiper;
    }

    /**
     * Swiper instance when slider autoplay is enabled (play/pause sync only)
     * @returns {object | null | undefined}
     */
    _getSwiperInstance() {
      const slider = this.closest(`${this.classes.slider}`);
      if (!slider || typeof slider._getSliderSettingsFromLiquid !== 'function') {
        return null;
      }
      const isAutoplayEnabled = slider._getSliderSettingsFromLiquid()?.autoplay?.base;
      return isAutoplayEnabled ? slider?.swiperInstance ?? null : null;
    }
  }

  customElements.define('wlsn-video', WLSNVideo);
}
